/**
 * CR-6724: tile-level Download Template action.
 *
 * Sits beside Upload on each import tile so a user can get the correct
 * spreadsheet without opening the upload flow first — the dialog carries the
 * same action alongside the column list.
 */
import { Button } from '../../components/ui/button';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../../components/ui/tooltip';
import { Download, Loader2 } from 'lucide-react';
import { useTemplateDownload } from './useTemplateDownload';

interface TemplateButtonProps {
  /** The import type this tile represents. */
  type: { step: number; name: string };
}

/**
 * Icon button that downloads the .xlsx template for one import type.
 *
 * @param type - Step number and display name of the import type
 */
export default function TemplateButton({ type }: TemplateButtonProps) {
  const { downloadingStep, downloadTemplate } = useTemplateDownload();
  const downloading = downloadingStep === type.step;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
            aria-label={`Download ${type.name} template`}
            disabled={downloading}
            onClick={(e) => {
              // The whole tile is clickable — without this the drill-down
              // modal opens behind the download.
              e.stopPropagation();
              void downloadTemplate(type.step, type.name);
            }}
          >
            {downloading ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
            ) : (
              <Download className="h-3.5 w-3.5" />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-xs">Download Excel template</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
